import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import SearchableSelect from '../components/SearchableSelect';
import ViewToggle from '../components/ViewToggle';
import { cardHeaderHue } from '../utils/cardTheme';
import { useViewPreference } from '../utils/viewPreference';
import { PAGE_SIZE_OPTIONS, buildPageList } from '../utils/pagination';

const EMPTY_FORM = { module_code: '', module_name: '', exam_weightage: '', pass_threshold: '', language: '', is_active: 1, scheme_id: null };

const LANGUAGE_OPTIONS = [
  { value: 'English', label: 'English', mark: 'EN' },
  { value: 'Chinese', label: 'Chinese', mark: '中' },
];

// Strip anything but digits so exam weightage / pass threshold can never carry decimals.
const toWholeNumberString = (raw) => raw.replace(/[^\d]/g, '');

// Display helper: round any stray decimal from the backend down to a whole percentage.
const wholePercent = (val) => Math.round(Number(val) || 0);

export default function ModuleMasterPage() {
  const { show } = useToast();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [sortKey, setSortKey] = useState('module_name');
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [view, setView] = useViewPreference('modules');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get('/modules')
      .then((res) => setModules(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load modules'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, languageFilter, pageSize]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const visibleModules = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = modules.filter((m) => {
      if (term && !m.module_code.toLowerCase().includes(term) && !m.module_name.toLowerCase().includes(term)) return false;
      if (statusFilter && (statusFilter === 'active') !== !!m.is_active) return false;
      if (languageFilter && m.language !== languageFilter) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === 'exam_weightage' || sortKey === 'pass_threshold') {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
        return (av - bv) * dir;
      }
      if (sortKey === 'is_active') {
        return ((av ? 1 : 0) - (bv ? 1 : 0)) * dir;
      }
      av = (av || '').toString().toLowerCase();
      bv = (bv || '').toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [modules, search, statusFilter, languageFilter, sortKey, sortDir]);

  const total = visibleModules.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return visibleModules;
    const start = (currentPage - 1) * effectivePageSize;
    return visibleModules.slice(start, start + effectivePageSize);
  }, [visibleModules, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa-solid fa-sort" style={{ color: 'var(--slate-lt)', marginLeft: '.35rem' }} />;
    return <i className={`fa-solid ${sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} style={{ color: 'var(--ocean)', marginLeft: '.35rem' }} />;
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  };

  const openEdit = (m) => {
    setForm({
      module_code: m.module_code,
      module_name: m.module_name,
      exam_weightage: String(wholePercent(m.exam_weightage)),
      pass_threshold: String(wholePercent(m.pass_threshold)),
      language: m.language || '',
      is_active: m.is_active ? 1 : 0,
      scheme_id: m.scheme_id ?? null,
    });
    setFormError(null);
    setModalMode('edit');
  };

  const toggleActive = async (m) => {
    try {
      await api.put(`/modules/${m.module_code}`, {
        module_name: m.module_name,
        exam_weightage: m.exam_weightage,
        pass_threshold: m.pass_threshold,
        language: m.language,
        scheme_id: m.scheme_id,
        is_active: m.is_active ? 0 : 1,
      });
      show(`Module ${m.is_active ? 'disabled' : 'enabled'}`, 'success');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const isDuplicateCode = useMemo(() => {
    if (modalMode !== 'create') return false;
    const code = form.module_code.trim().toLowerCase();
    if (!code) return false;
    return modules.some((m) => m.module_code.toLowerCase() === code);
  }, [modalMode, form.module_code, modules]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.language) {
      setFormError('Please select a language.');
      return;
    }
    if (modalMode === 'create' && isDuplicateCode) {
      setFormError('Module code already exists.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await api.post('/modules', form);
        show('Module created', 'success');
      } else {
        await api.put(`/modules/${form.module_code}`, form);
        show('Module updated', 'success');
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} module`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="sh-ey">PROGRAMMES · MODULE MASTER</div>
      <div className="sh-ti">Module Master</div>
      <div className="sh-de">Live module catalogue from the backend — each module carries an exam weightage, pass threshold, and optional marking scheme.</div>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 240px', minWidth: 200 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search code or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-filter" />
          <SearchableSelect
            className="finput" style={{ width: 170 }} value={statusFilter} onChange={setStatusFilter}
            placeholder="All Statuses"
            options={[{ value: '', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
          />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-language" />
          <SearchableSelect
            className="finput" style={{ width: 185 }} value={languageFilter} onChange={setLanguageFilter}
            placeholder="All Languages"
            options={[{ value: '', label: 'All Languages' }, ...LANGUAGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))]}
          />
        </div>
        {view === 'tiles' && (
          <div className="icon-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
            <i className="fa-solid fa-arrow-down-a-z" />
            <SearchableSelect
              className="finput" value={sortKey} onChange={setSortKey}
              options={[
                { value: 'module_code', label: 'Sort: Code' },
                { value: 'module_name', label: 'Sort: Name' },
                { value: 'exam_weightage', label: 'Sort: Exam Weightage' },
                { value: 'pass_threshold', label: 'Sort: Pass Threshold' },
                { value: 'language', label: 'Sort: Language' },
                { value: 'is_active', label: 'Sort: Status' },
              ]}
            />
          </div>
        )}
        {view === 'tiles' && (
          <button type="button" className="btn btn-outline btn-toolbar" title={sortDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short'}`} />
          </button>
        )}
        <ViewToggle view={view} onChange={setView} />
        <button className="btn btn-primary btn-toolbar" style={{ marginLeft: 'auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Module</button>
      </div>

      {loading && <Loader label="Loading modules…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !modules.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-layer-group" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No modules yet</div>
          <div style={{ color: 'var(--slate)', marginBottom: '1rem' }}>Add your first module to get started.</div>
          <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Module</button>
        </div>
      )}

      {!loading && !error && !!modules.length && !visibleModules.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No modules match the current search/filters.
        </div>
      )}

      {!loading && !error && !!visibleModules.length && view === 'table' && (
        <div className="card view-fade" key="table">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {[
                    ['module_code', 'Code'],
                    ['module_name', 'Name'],
                    ['exam_weightage', 'Exam Weightage'],
                    ['pass_threshold', 'Pass Threshold'],
                    ['language', 'Language'],
                    ['is_active', 'Status'],
                  ].map(([key, label]) => (
                    <th key={key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
                      {label}{sortIcon(key)}
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((m) => (
                  <tr key={m.module_code}>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 700, color: 'var(--ocean)' }}>{m.module_code}</span></td>
                    <td style={{ fontWeight: 600 }}>{m.module_name}</td>
                    <td>{wholePercent(m.exam_weightage)}%</td>
                    <td>{wholePercent(m.pass_threshold)}%</td>
                    <td>{m.language || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        <button
                          type="button"
                          className={`toggle ${m.is_active ? 'on' : ''}`}
                          role="switch"
                          aria-checked={!!m.is_active}
                          title={m.is_active ? 'Disable module' : 'Enable module'}
                          aria-label={m.is_active ? 'Disable module' : 'Enable module'}
                          onClick={() => toggleActive(m)}
                        />
                        <span className={`pill ${m.is_active ? 'p-ok' : 'p-draft'}`}>{m.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                    </td>
                    <td>
                      <button type="button" className="btn btn-outline btn-sm" title="Edit module" aria-label="Edit module" onClick={() => openEdit(m)}><i className="fa-solid fa-pen" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && !!visibleModules.length && view === 'tiles' && (
        <div className="g4 view-fade" key="tiles">
          {pageRows.map((m) => (
            <div key={m.module_code} className="cc-card">
              <div className="cc-hd cc-hd-tint" style={{ '--h': cardHeaderHue(m.module_code) }}>
                <div className="cc-em"><i className="fa-solid fa-book-open" /></div>
                <div style={{ minWidth: 0 }}>
                  <div className="cc-code">{m.module_code}</div>
                  <div className="cc-ti">{m.module_name}</div>
                </div>
              </div>
              <div className="cc-body">
                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--cloud)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '.5rem' }}>
                    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 800, color: 'var(--ink)' }}>{wholePercent(m.exam_weightage)}%</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>Exam Weightage</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--cloud)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '.5rem' }}>
                    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 800, color: 'var(--ink)' }}>{wholePercent(m.pass_threshold)}%</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>Pass Threshold</div>
                  </div>
                </div>
              </div>
              <div className="cc-foot">
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <button
                    type="button"
                    className={`toggle ${m.is_active ? 'on' : ''}`}
                    role="switch"
                    aria-checked={!!m.is_active}
                    title={m.is_active ? 'Disable module' : 'Enable module'}
                    aria-label={m.is_active ? 'Disable module' : 'Enable module'}
                    onClick={() => toggleActive(m)}
                  />
                  <span className={`pill ${m.is_active ? 'p-ok' : 'p-draft'}`}>{m.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}><i className="fa-solid fa-language" /> {m.language || '—'}</span>
                  <button type="button" className="btn btn-outline btn-sm" title="Edit module" aria-label="Edit module" onClick={() => openEdit(m)}><i className="fa-solid fa-pen" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && !!visibleModules.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.3rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
            Showing {rangeStart}–{rangeEnd} of {total}
          </div>
          <div className="icon-field" style={{ marginLeft: 'auto' }}>
            <SearchableSelect
              className="finput" style={{ width: 110 }} value={pageSize}
              onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
              options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
            />
          </div>
          {pageSize !== 'All' && totalPages > 1 && (
            <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
              {buildPageList(currentPage, totalPages).map((p, idx) => (
                p === '…'
                  ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                  : (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
              ))}
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
            </div>
          )}
        </div>
      )}

      {modalMode && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 460, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modalMode === 'create' ? 'Add Module' : 'Edit Module'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
            {formError && <div className="lc-err">{formError}</div>}
            <div className="fg fg-tight">
              <label className="fl">Module Code *</label>
              <input
                className="finput"
                required
                disabled={modalMode === 'edit'}
                value={form.module_code}
                onChange={(e) => setForm((f) => ({ ...f, module_code: e.target.value }))}
              />
              {modalMode === 'edit' && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)', marginTop: '.25rem' }}>Module code cannot be changed after creation</div>
              )}
              {modalMode === 'create' && isDuplicateCode && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: '.25rem' }}>Module code already exists.</div>
              )}
            </div>
            <div className="fg fg-tight"><label className="fl">Module Name *</label><input className="finput" required value={form.module_name} onChange={(e) => setForm((f) => ({ ...f, module_name: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: '.8rem' }}>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Exam Weightage (%) *</label><input className="finput" type="number" min="0" max="100" step="1" inputMode="numeric" required value={form.exam_weightage} onChange={(e) => setForm((f) => ({ ...f, exam_weightage: toWholeNumberString(e.target.value) }))} /></div>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Pass Threshold (%) *</label><input className="finput" type="number" min="0" max="100" step="1" inputMode="numeric" placeholder="50" required value={form.pass_threshold} onChange={(e) => setForm((f) => ({ ...f, pass_threshold: toWholeNumberString(e.target.value) }))} /></div>
            </div>
            <div className="fg fg-tight">
              <label className="fl">Language *</label>
              <div className="pick-grid">
                {LANGUAGE_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    className={`pick-card ${form.language === opt.value ? 'sel' : ''}`}
                    aria-pressed={form.language === opt.value}
                    onClick={() => setForm((f) => ({ ...f, language: opt.value }))}
                  >
                    <span className="pick-check" aria-hidden="true"><i className="fa-solid fa-check" /></span>
                    <span className="pick-icon" aria-hidden="true">{opt.mark}</span>
                    <span className="pick-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeModal}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (modalMode === 'create' && isDuplicateCode)}>
                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {submitting ? 'Saving…' : modalMode === 'create' ? 'Create Module' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
